import get from 'lodash/get';
import throttle from 'lodash/throttle';
import uniq from 'lodash/uniq';
import { nodesBBox } from './utils'
import GroupComponent from '../../nodes/GroupComponent';
import Group from './group';

function install(editor) {
  let groupNodes = [];
  const groups = new Map();

  const groupComponent = new GroupComponent(editor);
  editor.register(groupComponent);

  editor.events.resizeEnd = [];

  // Add a group object that tracks dragging of a group node to
  // also drag child nodes along with it
  editor.on('nodecreated', node => {
    if (node.name === 'Group') {
      node.meta = { isDragTarget: false };
      const viewNode = editor.view.nodes.get(node);
      const group = new Group(editor, viewNode.el);
      group.linkTo(node.data.childIds);
      groups.set(node.id, group);
      groupNodes.push(node);
    }
    // If new node overlaps with a group, add it to the group
    const groupNode = findFirstOverlapping(node);
    if (groupNode && !groupNode.data.childIds.includes(node.id)) {
      // console.log('about to addToGroup')
      addToGroup(node, groupNode);
    }
  });

  editor.on('noderemoved', node => {
    if (node.name === 'Group') {
      groups.delete(node.id);
      groupNodes = groupNodes.filter(g => g.id !== node.id);
    }
  });

  editor.events.collapse = [];
  editor.on('collapse', ({ node, isCollapsed }) => {
    const viewNode = editor.view.nodes.get(node);
    viewNode.el.style.zIndex = isCollapsed ? 5 : -1;
  });

  // On receiving an addGroup event, create a new group node
  // with the selected nodes as children.
  editor.events.addGroup = [];
  editor.on('addGroup', async () => {
    if (areCandidateChildren(editor.selected.list)) {
      const bbox = nodesBBox(editor, editor.selected.list, 36);
      const groupNode = await groupComponent.createNode();
      groupNode.position = [bbox.left, bbox.top - 36];
      groupNode.data.width = bbox.width;
      groupNode.data.height = bbox.height;
      groupNode.data.childIds = editor.selected.list.map(n => n.id);
      groupNode.data.newlyCreated = true;
      editor.addNode(groupNode);
    } else {
      if (editor.selected.list.length) {
        alert('Cannot create group.  One or more nodes is a group or already belong to a group.')
      }
    }
  });

  // Only allow dragging the title bar of group nodes
  editor.on('selectnode', ({ node, event }) => {
    if (node.name === 'Group' && event && !event.target.classList.contains('group-draggable')) {
      return false;
    }
  });

  // Handle resizing of group borders
  editor.on('resizeEnd', node => {
    onGroupResize(node);
  });

  // Handle dragging of child nodes
  let drags = {};

  editor.on('nodetranslate', ({ node }) => {
    if (!isFrameOrGroup(node) && !drags[node.id]) {
      drags[node.id] = {
        node
      };
      childDragStart(drags[node.id]);
    } else if (!isFrameOrGroup(node) && drags[node.id]) {
      childDragMoveThrottled(drags[node.id]);
    }
  });

  editor.on('nodedraged', (node) => {
    if (!isFrameOrGroup(node)) {
      for (const value of Object.values(drags)) {
        // console.log('about to childDragEnd')
        childDragEnd(value);
      }
    }
    drags = {};
  });

  function isFrameOrGroup(node) {
    return node.name === 'Frame' || node.name === 'Group';
  }

  function onGroupResize(groupNode) {
    const childNodes = [];
    editor.nodes.forEach(childNode => {
      if (!isFrameOrGroup(childNode) && isRectInRect(getRect(groupNode), getRect(childNode))) {
        const parentNode = getParentGroup(childNode);
        if (!parentNode || parentNode.id === groupNode.id) {
          childNodes.push(childNode);
        }
      }
    });
    setGroupChildren(childNodes, groupNode);
  }

  function getParentGroup(node) {
    let parentNode;
    groupNodes.forEach(groupNode => {
      if ((groupNode.data.childIds || []).includes(node.id)) {
        parentNode = groupNode;
      }
    })
    return parentNode;
  }

  function childDragStart(drag) {
    const groupNode = findFirstOverlapping(drag.node);
    drag.isDragging = true;
    drag.fromGroup = groupNode;
    drag.toGroup = null;
  }

  function childDragMove(drag) {
    if (!drag.isDragging) {
      return; // sometimes this can happen: [dragMove, dragMove, ..., dragEnd, dragMove ]
              // so ignore the last drag move
    }
    const groupNode = findFirstOverlapping(drag.node);
    if (groupNode && (!drag.fromGroup || groupNode.id !== drag.fromGroup.id)) {
      drag.toGroup = groupNode;
    }
    setDragTarget(drag.toGroup, groupNode);
  }

  const childDragMoveThrottled = throttle(function (drag) {
    childDragMove(drag);
  }, 50);

  function childDragEnd(drag) {
    drag.isDragging = false;
    setDragTarget(drag.toGroup, null);

    const groupNode = findFirstOverlapping(drag.node);
    if (groupNode && groupNode.data.isCollapsed) {
      return;
    }

    const needToRemove = drag.fromGroup && (!groupNode || groupNode.id !== drag.fromGroup.id);
    const needToAdd = groupNode && (!drag.fromGroup || groupNode.id !== drag.fromGroup.id);
    if (needToRemove) {
      removeFromGroup(drag.node, drag.fromGroup);
    }
    if (needToAdd) {
      // console.log('about to addToGroup')
      addToGroup(drag.node, groupNode);
    }
  }

  function findFirstOverlapping(node) {
    const nodeRect = getRect(node);
    // Search parent node first
    const groupNode = getParentGroup(node);
    if (groupNode) {
      const groupRect = getRect(groupNode);
      if (isRectInRect(groupRect, nodeRect)) {
        return groupNode;
      }
    }
    // Then search rest of groups
    for (const groupNode of groupNodes) {
      const groupRect = getRect(groupNode);
      if (isRectInRect(groupRect, nodeRect)) {
        return groupNode;
      }
    }
    return null;
  }

  function setDragTarget(node, overlappingNode) {
    if (node) {
      node.meta.isDragTarget = overlappingNode && overlappingNode.id === node.id;
    }
  }

  function getRect(node) {
    const viewNode = editor.view.nodes.get(node);
    return {
      x: node.position[0],
      y: node.position[1],
      w: viewNode.el.clientWidth,
      h: viewNode.el.clientHeight,
    };
  }

  function isRectInRect(rect1, rect2) {
    if (
      rect2.x + rect2.w < rect1.x + rect1.w &&
      rect2.x > rect1.x &&
      rect2.y > rect1.y &&
      rect2.y + rect2.h < rect1.y + rect1.h
    ) {
      return true;
    } else {
      return false;
    }
  }

  function addToGroup(node, groupNode) {
    const group = groups.get(groupNode.id);
    if (group) {
      let updatedIds = cleanChildIds(groupNode.data.childIds, editor.caseConf);
      updatedIds = updatedIds.concat(node.id);
      groupNode.data.childIds = updatedIds;
      // console.log(groupNode.data.childIds)
      group.linkTo(updatedIds);
    }
  }

  function removeFromGroup(node, groupNode) {
    groupNode.data.childIds = groupNode.data.childIds.filter(id => id !== node.id);
    const group = groups.get(groupNode.id);
    if (group) {
      group.linkTo(groupNode.data.childIds);
    }
  }

  function setGroupChildren(children, groupNode) {
    groupNode.data.childIds = children.map(child => child.id);
    const group = groups.get(groupNode.id);
    if (group) {
      group.linkTo(groupNode.data.childIds);
    }
  }

  function areCandidateChildren(nodeList) {
    // Nodes can only be added to a group if they aren't groups, and don't
    // belong to an existing group
    if (!nodeList.length) {
      return false;
    }

    for (const node of nodeList) {
      if (node.name === 'Group') {
        return false;
      }

      const parent = getParentGroup(node);
      if (parent) {
        return false;
      }
    }

    return true;
  }

  function cleanChildIds(childIds, caseConf) {
    const nodeMap = get(caseConf, 'caseLogicDescription.nodes') || {};
    return uniq(
      childIds.filter(id => nodeMap[id])
    );
  }
}

export default {
  name: 'group',
  install
}
